import { Loader2, Sparkles, AlertTriangle, RefreshCw, Clock, Check } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '@/lib/utils';
import { fmtGBP, toPence } from './LineItemsTable';
import { isReadTimedOut } from './inboxFormat';

const field = 'bg-[#1E2530] border-white/20 text-white h-9';
const asInput = (pence) => (pence > 0 ? (pence / 100).toFixed(2) : '');

// One clear, obviously-clickable button; its arrow spins while the read runs.
function RereadButton({ onReread, disabled, working, idle, workingLabel, className, testid }) {
  return (
    <button type="button" onClick={onReread} disabled={disabled}
      className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors shrink-0 disabled:cursor-wait', className)}
      title="Re-read this invoice with AI" data-testid={testid}>
      <RefreshCw className={cn('w-3.5 h-3.5', working && 'animate-spin')} />
      {working ? workingLabel : idle}
    </button>
  );
}

export function AiReadStatus({ item, dirty, busy, reading, onApply, onReread }) {
  const st = item.extraction_status;
  const base = 'rounded-lg px-3 py-2 text-xs flex items-center gap-2';
  if (isReadTimedOut(item)) {
    return (
      <div className={`${base} bg-amber-500/10 text-amber-200 border border-amber-500/20`} data-testid="ai-status-timeout">
        <Clock className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1">The AI read is taking longer than expected — you can retry, or just key the invoice in below.</span>
        <RereadButton onReread={onReread} disabled={busy} working={reading} idle="Retry" workingLabel="Retrying…" className="border-amber-400/50 bg-amber-400/10 hover:bg-amber-400/20 text-amber-100" testid="ai-timeout-retry-btn" />
      </div>
    );
  }
  if (st === 'pending' || st === 'running') {
    return <div className={`${base} bg-[#F5A623]/10 text-[#F5A623] border border-[#F5A623]/20`} data-testid="ai-status-reading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI is reading the invoice — fields and lines will fill in shortly.</div>;
  }
  if (st === 'failed') {
    return (
      <div className={`${base} bg-red-500/10 text-red-200 border border-red-500/20`} data-testid="ai-status-failed">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1 truncate" title={item.extraction_error || ''}>AI read failed — fill the fields in by hand{item.extraction_error ? ` (${item.extraction_error})` : ''}.</span>
        <RereadButton onReread={onReread} disabled={busy} working={reading} idle="Retry" workingLabel="Retrying…" className="border-red-400/50 bg-red-400/10 hover:bg-red-400/20 text-red-100" testid="ai-retry-btn" />
      </div>
    );
  }
  if (st === 'done') {
    const ex = item.extraction || {};
    const mem = ex.memory;
    return (
      <div className={`${base} bg-emerald-500/10 text-emerald-200 border border-emerald-500/20`} data-testid="ai-status-done">
        <Sparkles className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1">
          {reading ? 'Re-reading the invoice with AI…' : (<>
            AI read complete{ex.document_type && ex.document_type !== 'invoice' ? (ex.document_type === 'other' ? ' · document type unclear' : ` · looks like ${/^[aeiou]/i.test(ex.document_type) ? 'an' : 'a'} ${ex.document_type.replace('_', ' ')}`) : ''}
            {mem?.matched && <span className="text-emerald-300/80" data-testid="ai-memory-note"> · used memory from {mem.invoices} past invoice{mem.invoices === 1 ? '' : 's'} from {mem.supplier}</span>}
            {' '}— check every line, then post.
          </>)}
        </span>
        {dirty && !reading && <button type="button" onClick={onApply} disabled={busy} className="underline shrink-0 disabled:opacity-50" data-testid="ai-apply-btn">Apply suggestions</button>}
        <RereadButton onReread={onReread} disabled={busy} working={reading} idle="Re-read" workingLabel="Re-reading…" className="border-emerald-400/40 bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-100" testid="ai-reread-btn" />
      </div>
    );
  }
  if (!st) {
    return (
      <div className={`${base} bg-white/5 text-gray-300 border border-white/10`} data-testid="ai-status-none">
        <Sparkles className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1">{reading ? 'Reading the invoice with AI…' : 'Not read by AI yet.'}</span>
        <RereadButton onReread={onReread} disabled={busy} working={reading} idle="Read with AI" workingLabel="Reading…" className="border-[#F5A623]/50 bg-[#F5A623]/15 hover:bg-[#F5A623]/25 text-[#F5A623]" testid="ai-read-btn" />
      </div>
    );
  }
  return null;
}

const money = (pounds) => (pounds == null ? null : Math.round(Number(pounds) * 100));

/** Till receipts with a total but no VAT breakdown: one tap to treat it as VAT-free, or to split a 20% / 5% inclusive total. */
function VatQuickActions({ netPence, aiGross, onNoVat, onIncluded }) {
  // The figure to split: the reader's gross if it saw one, otherwise whatever was typed into Net (a till total).
  const base = aiGross || (netPence > 0 ? netPence : 0);
  const btn = 'px-2 py-0.5 rounded border text-[11px] transition-colors';
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-400" data-testid="inbox-vat-quick">
      <span>No VAT breakdown on the receipt?</span>
      <button type="button" onClick={onNoVat} className={`${btn} border-white/15 bg-white/5 text-gray-200 hover:bg-white/10`} data-testid="inbox-vat-none">No VAT · 0%</button>
      {base > 0 && [20, 5].map(rate => {
        const net = Math.round((base * 100) / (100 + rate));
        return (
          <button key={rate} type="button" onClick={() => onIncluded(base, rate)} className={`${btn} border-[#F5A623]/40 bg-[#F5A623]/10 text-[#F5A623] hover:bg-[#F5A623]/20`}
            title={`Total ${fmtGBP(base)} includes ${rate}% VAT → Net ${fmtGBP(net)} + VAT ${fmtGBP(base - net)}`} data-testid={`inbox-vat-included-${rate}`}>
            Total {fmtGBP(base)} includes {rate}% VAT
          </button>
        );
      })}
    </div>
  );
}

/**
 * Header: supplier / ref / date + the three invoice totals. Net (ex VAT) is THE figure —
 * line items are ex VAT and must add up to it. Gross is never typed: it is always Net + VAT.
 * VAT the reader couldn't find stays blank and flagged — it is never assumed to be £0.
 */
export function InvoiceHeaderFields({ form, setForm, aiTotals, readOnly, totalMode = 'imported', setTotalMode = () => {}, linesTotalPence = 0, onNetChange, rateIssue, cis = null }) {
  const cisOn = !!cis?.enabled;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const netPence = toPence(form.net), vatKnown = form.vat !== '', vatPence = toPence(form.vat), grossPence = netPence + vatPence;
  const setNet = (value) => onNetChange(value, 'manual');
  const setVat = (value) => setForm(f => ({ ...f, vat: value, vat_status: value === '' ? 'unknown' : 'ok', vat_note: '' }));
  const aiNet = money(aiTotals?.net_pounds), aiVat = money(aiTotals?.vat_pounds), aiGross = money(aiTotals?.gross_pounds);
  const hasAi = !!aiTotals;
  const aiDiffers = hasAi && !cisOn && ((aiNet ?? -1) !== netPence || (aiVat == null ? vatKnown : aiVat !== vatPence));
  const useAi = () => {
    setTotalMode('imported');
    setForm(f => ({ ...f, net: aiNet != null ? asInput(aiNet) : '', vat: aiVat != null ? (aiVat / 100).toFixed(2) : '',
      vat_status: aiVat == null ? 'unknown' : aiTotals.reconciles ? 'ok' : 'check',
      vat_note: aiVat == null ? "VAT not read — enter it from the invoice (0 if there's none)" : aiTotals.reconciles ? '' : "The reader's Net + VAT doesn't equal its Gross — check the totals against the invoice" }));
  };
  const linesDiffer = linesTotalPence > 0 && linesTotalPence !== netPence;
  const vatRate = netPence > 0 && vatKnown ? Math.round((vatPence / netPence) * 1000) / 10 : null;
  const flagged = !readOnly && !cisOn && (form.vat_status === 'unknown' || form.vat_status === 'check' || !vatKnown);
  const num = `${field} text-right tabular-nums no-spinner`;
  return (
    <div className="grid grid-cols-2 gap-2.5" data-testid="inbox-header-fields">
      <div>
        <label className="text-xs text-gray-400">Supplier</label>
        <Input value={form.supplier_name} onChange={set('supplier_name')} disabled={readOnly} className={field} placeholder="e.g. Travis Perkins" data-testid="inbox-supplier-input" />
      </div>
      <div>
        <label className="text-xs text-gray-400">Reference / invoice no.</label>
        <Input value={form.reference} onChange={set('reference')} disabled={readOnly} className={field} data-testid="inbox-reference-input" />
      </div>
      <div>
        <label className="text-xs text-gray-400">Invoice date</label>
        <Input type="date" value={form.invoice_date} onChange={set('invoice_date')} disabled={readOnly} className={field} data-testid="inbox-date-input" />
      </div>
      <div className="col-span-2 rounded-lg border border-white/10 bg-white/[0.03] p-2.5" data-testid="inbox-totals-block">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-xs text-gray-300 font-medium">Invoice totals</span>
          <span className="text-[10px] uppercase tracking-wider text-[#F5A623]" data-testid="inbox-totals-basis">Lines are ex VAT · must add up to Net</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[11px] text-[#F5A623] font-semibold">Net (ex VAT) *</label>
            <Input type="number" step="0.01" inputMode="decimal" value={form.net} onChange={e => setNet(e.target.value)} disabled={readOnly}
              className={`${num} ring-1 ring-[#F5A623]/50 ${netPence <= 0 && !readOnly ? 'border-amber-500/50' : ''}`} placeholder="0.00" data-testid="inbox-net-input" />
          </div>
          <div>
            <label className="text-[11px] text-gray-400">VAT{cisOn ? <span className="text-[#F5A623]"> · CIS {cis.vat_treatment === 'reverse_charge' ? 'reverse charge 0%' : 'no VAT'}</span> : vatRate != null ? <span className="text-gray-500"> · {vatRate}%</span> : ''}</label>
            <Input type="number" step="0.01" inputMode="decimal" value={cisOn ? '0.00' : form.vat} onChange={e => setVat(e.target.value)} disabled={readOnly || cisOn}
              title={cisOn ? 'CIS invoices carry no VAT (not registered) or reverse-charge VAT at 0%' : ''}
              className={`${num} ${flagged ? 'border-amber-500/60 ring-1 ring-amber-500/30' : ''} ${cisOn ? 'border-[#F5A623]/40 text-gray-400' : ''}`} placeholder={vatKnown ? '0.00' : 'not read'} data-testid="inbox-vat-input" />
          </div>
          <div>
            <label className="text-[11px] text-gray-400">Gross (inc VAT) <span className="text-gray-600">= Net + VAT</span></label>
            <div className={`${num} flex items-center justify-end rounded-md border px-3 ${vatKnown ? 'text-gray-200' : 'text-gray-500'}`} title="Worked out from Net + VAT — not typed" data-testid="inbox-gross-value">
              {vatKnown || netPence > 0 ? fmtGBP(grossPence).replace('£', '') : '—'}{!vatKnown && netPence > 0 ? ' + VAT?' : ''}
            </div>
          </div>
        </div>
        {!readOnly && (
          <div className="text-[11px] text-gray-400 mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5" data-testid="inbox-total-mode">
            {totalMode === 'auto' && <span>Net follows your line items · type to override</span>}
            {totalMode === 'imported' && <span>{hasAi ? 'From the AI read' : 'As saved'} · editing lines replaces the net</span>}
            {totalMode === 'manual' && <span>Net entered by you</span>}
            {linesDiffer && totalMode !== 'auto' && <button type="button" className="text-[#F5A623] hover:underline" onClick={() => onNetChange(asInput(linesTotalPence), 'auto')} data-testid="inbox-match-lines">Match lines ({fmtGBP(linesTotalPence)})</button>}
            {hasAi && (
              <span className="ml-auto" data-testid="inbox-ai-totals">
                AI read: Net {aiNet != null ? fmtGBP(aiNet) : '—'} · VAT {aiVat != null ? fmtGBP(aiVat) : 'not read'} · Gross {aiGross != null ? fmtGBP(aiGross) : '—'}
                {aiDiffers && <button type="button" className="text-[#F5A623] hover:underline ml-1.5" onClick={useAi} data-testid="inbox-use-ai-totals">Use these</button>}
              </span>
            )}
          </div>
        )}
        {!readOnly && (
          <div className="mt-1.5 space-y-1" data-testid="inbox-reconcile">
            {vatKnown && netPence > 0 && (
              <div className="text-[11px] text-emerald-300/80 flex items-center gap-1" data-testid="inbox-reconcile-ok"><Check className="w-3 h-3" /> Net {fmtGBP(netPence)} + VAT {fmtGBP(vatPence)} = Gross {fmtGBP(grossPence)}</div>
            )}
            {hasAi && !aiTotals.reconciles && (
              <div className="text-[11px] text-amber-200 flex items-start gap-1.5" data-testid="inbox-ai-mismatch"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> The reader's own figures don't add up (Net + VAT ≠ Gross) — check the totals against the invoice.</div>
            )}
            {flagged && (
              <div className="text-[11px] text-amber-200 flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/25 rounded px-2 py-1" data-testid="inbox-vat-flag">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{!vatKnown ? "VAT not read — enter it from the invoice (0 if there's none) before posting." : form.vat_note || 'Check the VAT against the invoice.'}
                  {vatKnown && form.vat_status === 'check' && <button type="button" className="text-emerald-300 hover:underline ml-1.5" onClick={() => setForm(f => ({ ...f, vat_status: 'ok', vat_note: '' }))} data-testid="inbox-vat-confirm">VAT is right</button>}
                </span>
              </div>
            )}
            {!vatKnown && (
              <VatQuickActions netPence={netPence} aiGross={aiGross} onNoVat={() => {
                if (netPence <= 0 && aiGross) onNetChange(asInput(aiGross), 'manual');
                setForm(f => ({ ...f, vat: '0.00', vat_status: 'ok', vat_note: '' }));
              }} onIncluded={(base, rate) => {
                const net = Math.round((base * 100) / (100 + rate));
                onNetChange(asInput(net), 'manual');
                setForm(f => ({ ...f, vat: asInput(base - net), vat_status: 'ok', vat_note: '' }));
              }} />
            )}
            {rateIssue && <div className="text-[11px] text-red-200 flex items-start gap-1.5" data-testid="inbox-rate-issue"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {rateIssue}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
